import { computed, ref } from 'vue'

import { defineStore } from 'pinia'

import type { CommunityUserGroup } from '../types/community-user'

/**
 * Community User UI Store - Handles UI-only state for user profiles and user-group relationships
 */
export const useCommunityUserUIStore = defineStore('community-user-ui', () => {
  // ===== UI STATE =====

  const currentCommunityUser = ref<CommunityUserGroup | null>(null)
  const contributionFilter = ref<'post' | 'posts-and-comments'>('post')
  const communitiesFilter = ref<'all' | 'joined' | 'mutual'>('all')
  const isInitialized = ref(false)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // ===== ACTIONS =====

  const setCurrentCommunityUser = (user: CommunityUserGroup | null) => {
    currentCommunityUser.value = user
  }

  const setContributionFilter = (filter: 'post' | 'posts-and-comments') => {
    contributionFilter.value = filter
  }

  const setCommunitiesFilter = (filter: 'all' | 'joined' | 'mutual') => {
    communitiesFilter.value = filter
  }

  const setInitialized = (value: boolean) => {
    isInitialized.value = value
  }

  const setLoading = (loading: boolean) => {
    isLoading.value = loading
  }

  const setError = (err: string | null) => {
    error.value = err
  }

  const reset = () => {
    currentCommunityUser.value = null
    contributionFilter.value = 'post'
    communitiesFilter.value = 'all'
    isInitialized.value = false
    isLoading.value = false
    error.value = null
  }

  // ===== GETTERS =====
  const hasCommunityUser = computed(() => !!currentCommunityUser.value)
  const userRole = computed(() => currentCommunityUser.value?.role || null)

  // ===== PUBLIC API =====
  return {
    // State (wrapped in computed per query-store-pattern)
    currentCommunityUser: computed(() => currentCommunityUser.value),
    contributionFilter: computed(() => contributionFilter.value),
    communitiesFilter: computed(() => communitiesFilter.value),
    isInitialized: computed(() => isInitialized.value),
    isLoading: computed(() => isLoading.value),
    error: computed(() => error.value),

    // Getters
    hasCommunityUser,
    userRole,

    // Actions
    setCurrentCommunityUser,
    setContributionFilter,
    setCommunitiesFilter,
    setInitialized,
    setLoading,
    setError,
    reset,
  }
})
